import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function VendorFormDialog({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(defaultForm(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(defaultForm(initial));
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const setF = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.name.trim()) { setError("Vendor name is required"); return; }
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await api.patch(`/spares/vendors/${initial.id}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/spares/vendors", form);
        toast.success("Vendor created");
      }
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save vendor");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="vendor-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
           data-testid="vendor-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">{isEdit ? "Edit Vendor" : "Add Vendor"}</h2>
            <p className="text-[11px] text-[#667085]">Contact + tax details in one place.</p>
          </div>
          <button onClick={onClose}
                  data-testid="vendor-dialog-close"
                  className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]">
            <X size={17} />
          </button>
        </header>

        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="vendor-dialog-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Vendor name *">
              <input type="text" value={form.name} onChange={(e) => setF("name", e.target.value)}
                     data-testid="vendor-name"
                     placeholder="e.g. Sri Ram Auto Parts"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="Contact person">
              <input type="text" value={form.contact_person} onChange={(e) => setF("contact_person", e.target.value)}
                     data-testid="vendor-contact-person"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="Phone">
              <input type="text" value={form.phone} onChange={(e) => setF("phone", e.target.value)}
                     data-testid="vendor-phone"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <Field label="WhatsApp">
              <input type="text" value={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.value)}
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)}
                     data-testid="vendor-email"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="GSTIN">
              <input type="text" value={form.gstin} onChange={(e) => setF("gstin", e.target.value.toUpperCase())}
                     data-testid="vendor-gstin"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
          </div>

          <Field label="Address">
            <textarea rows={2} value={form.address} onChange={(e) => setF("address", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <input type="text" value={form.city} onChange={(e) => setF("city", e.target.value)}
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="State">
              <input type="text" value={form.state} onChange={(e) => setF("state", e.target.value)}
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="PIN">
              <input type="text" value={form.pin} onChange={(e) => setF("pin", e.target.value)}
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
          </div>
          <Field label="Payment terms">
            <input type="text" value={form.payment_terms} onChange={(e) => setF("payment_terms", e.target.value)}
                   placeholder="Net 30 · COD · Advance"
                   className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
          </Field>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </Field>
          <label className="inline-flex items-center gap-2 text-[13px] text-[#1d3557]">
            <input type="checkbox" checked={form.active} onChange={(e) => setF("active", e.target.checked)}
                   data-testid="vendor-active" className="w-4 h-4 accent-[#e63946]" />
            Vendor is active
          </label>
        </div>

        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.name.trim()}
                  data-testid="vendor-save-btn"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create vendor")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function defaultForm(initial) {
  return {
    name: initial?.name || "",
    contact_person: initial?.contact_person || "",
    phone: initial?.phone || "",
    whatsapp: initial?.whatsapp || "",
    email: initial?.email || "",
    address: initial?.address || "",
    city: initial?.city || "",
    state: initial?.state || "",
    pin: initial?.pin || "",
    gstin: initial?.gstin || "",
    payment_terms: initial?.payment_terms || "",
    notes: initial?.notes || "",
    active: initial?.active !== false,
  };
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
